import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [authData, setAuthData] = useState(() => {
        const storedData = localStorage.getItem('authData');
        return storedData ? JSON.parse(storedData) : null;
    });

    const [userData, setUserData] = useState(() => {
        const storedUserData = localStorage.getItem('userData');
        return storedUserData ? JSON.parse(storedUserData) : null;
    });

    const login = (authResult, userDetails) => {
        if (!authResult) return;

        // Store tokens and authentication data
        const authInfo = {
            accessToken: authResult.AccessToken,
            refreshToken: authResult.RefreshToken,
            idToken: authResult.IdToken,
            expiresIn: authResult.ExpiresIn,
            tokenType: authResult.TokenType,
            expirationTime: new Date().getTime() + authResult.ExpiresIn * 1000, // Convert to milliseconds
        };

        // Store user details (role, organizationId, etc.)
        const userInfo = {
            email: userDetails.email,
            sub: userDetails.sub,
            role: userDetails.backpackEMRRole, // Add role
            organizationId: userDetails.OrganizationId, // Add organization ID
        };

       
        setAuthData(authInfo);
        setUserData(userInfo);

        // Store in localStorage
        localStorage.setItem('authData', JSON.stringify(authInfo));
        localStorage.setItem('userData', JSON.stringify(userInfo));
    };

    const logout = () => {
        setAuthData(null);
        setUserData(null);
        localStorage.removeItem('authData');
        localStorage.removeItem('userData');
    };

    const isAuthenticated = () => {
        if (!authData?.accessToken) {
            console.log('No access token found');
            return false;
        }

        const now = new Date().getTime();
        const isExpired = now >= authData.expirationTime;

        if (isExpired) {
            console.log('Token is expired');
            logout();
            return false;
        }

        return true;
    };

    // Automatically check token expiration
    useEffect(() => {
        if (authData?.expirationTime) {
            const now = new Date().getTime();
            const timeUntilExpiry = authData.expirationTime - now;

            if (timeUntilExpiry > 0) {
                const timer = setTimeout(() => {
                    logout();
                }, timeUntilExpiry);

                return () => clearTimeout(timer);
            }
        }
    }, [authData]);

    return (
        <AuthContext.Provider
            value={{
                authData,
                userData,
                login,
                logout,
                isAuthenticated,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};